// Profile: "Would you recommend RouteZero?" — thumbs row → follow-up (testimonial, public name,
// consent) → thank-you in the same grid cell. The vote is saved on click; the words on Send.
// Thumb icons: Lucide (ISC licence) thumbs-up / thumbs-down outlines.
import { h, svg } from './h.js';
import { MAX_BODY, MAX_NAME } from '../core/testimonials.js';

const THUMB = {
  up: 'M7 10v12M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88z',
  down: 'M17 14V2M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88z',
};
const lucide = (d, w = 2) => svg('svg', { viewBox: '0 0 24 24', 'aria-hidden': 'true', fill: 'none', stroke: 'currentColor', 'stroke-width': w, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, svg('path', { d }));
const STATUS = {
  pending: 'Waiting for review',
  approved: 'Approved',
  rejected: 'Not published',
};
/** "Ana Reyes" → "Ana R." (the learner can change it) */
export const shortName = (name = '') => { const [a, ...rest] = String(name).trim().split(/\s+/); return (rest.length ? `${a} ${rest.at(-1)[0]}.` : a || '').slice(0, MAX_NAME); };

/**
 * @param {{ service: ReturnType<import('../core/testimonials.js').createTestimonials>, user: {name?:string}|null }} o
 */
export function testimonialCard({ service, user }) {
  let vote = 0;
  let consentTouched = false;
  const up = h('button', { class: 'rz-tm-thumb', type: 'button', 'aria-label': 'Yes, I would recommend RouteZero', 'aria-pressed': 'false', 'data-testid': 'testimonial-up' }, lucide(THUMB.up));
  const down = h('button', { class: 'rz-tm-thumb', type: 'button', 'aria-label': 'No, I would not recommend RouteZero yet', 'aria-pressed': 'false', 'data-testid': 'testimonial-down' }, lucide(THUMB.down));
  const text = h('textarea', { class: 'rz-tm-text', id: 'rz-tm-text', rows: 3, maxlength: MAX_BODY, 'data-testid': 'testimonial-text', placeholder: 'What did RouteZero help you with?' });
  const nameIn = h('input', { class: 'rz-tm-name', id: 'rz-tm-name', maxlength: MAX_NAME, autocomplete: 'off', 'data-testid': 'testimonial-name', value: shortName(user?.name) });
  const consent = h('input', { type: 'checkbox', id: 'rz-tm-consent', 'data-testid': 'testimonial-consent', onchange: () => { consentTouched = true; } });
  const consentRow = h('label', { class: 'rz-tm-consent', for: 'rz-tm-consent' }, consent, h('span', {}, 'RouteZero may show this on its website with the name above.'));
  const count = h('span', { class: 'rz-tm-count', 'aria-live': 'polite' }, `0 / ${MAX_BODY}`);
  const error = h('p', { class: 'rz-tm-error', role: 'alert', 'data-testid': 'testimonial-error' });
  const send = h('button', { class: 'rz-tm-send', type: 'button', 'data-testid': 'testimonial-send' }, 'Send');
  const status = h('p', { class: 'rz-tm-status', 'data-testid': 'testimonial-status' });
  const thanksTitle = h('b', {}, 'Thank you');
  const thanksText = h('span', { 'data-testid': 'testimonial-thanks-text' }, '');
  const again = h('button', { class: 'rz-tm-again', type: 'button', 'data-testid': 'testimonial-edit' }, 'Edit my answer');
  const thanks = h('div', { class: 'rz-tm-thanks', role: 'status', 'aria-hidden': 'true', 'data-testid': 'testimonial-thanks' },
    h('span', { class: 'rz-tm-check' }, lucide('M5 12.5l4.5 4.5L19 7.5', 3)), thanksTitle, thanksText, again);

  const main = h('div', { class: 'rz-tm-main' },
    h('div', { class: 'rz-tm-row' },
      h('b', { class: 'rz-tm-q', id: 'rz-tm-q' }, 'Would you recommend RouteZero?'),
      h('div', { class: 'rz-tm-thumbs', role: 'group', 'aria-labelledby': 'rz-tm-q' }, up, down)),
    status,
    h('div', { class: 'rz-tm-ex' }, h('div', { class: 'rz-tm-exi' }, h('div', { class: 'rz-tm-pd' },
      h('label', { class: 'rz-tm-label', for: 'rz-tm-text' }, 'In your own words', h('small', {}, ' (optional)')), text,
      h('div', { class: 'rz-tm-meta' }, h('label', { class: 'rz-tm-label', for: 'rz-tm-name' }, 'Name to show'), count),
      nameIn, consentRow,
      h('div', { class: 'rz-tm-act' }, h('span', { class: 'rz-tm-hint' }, 'An admin reviews every testimonial before it appears.'), send),
      error))));
  const card = h('section', { class: 'rz-career-card panel rz-tm', 'data-testid': 'profile-testimonial', 'aria-labelledby': 'rz-tm-title' },
    h('div', { class: 'eyebrow', id: 'rz-tm-title' }, 'SHARE YOUR EXPERIENCE'),
    h('div', { class: 'rz-tm-stack' }, main, thanks));

  const press = (b) => { b.classList.add('press'); setTimeout(() => b.classList.remove('press'), 90); };
  const showVote = () => {
    for (const [b, v] of [[up, 1], [down, -1]]) { b.classList.toggle('sel', vote === v); b.setAttribute('aria-pressed', vote === v ? 'true' : 'false'); }
    text.placeholder = vote === 1 ? 'What did RouteZero help you with?' : 'What was missing? What should we improve?';
    consentRow.hidden = vote !== 1;
    card.classList.toggle('open', vote !== 0);
  };
  const setStatus = (row) => {
    status.textContent = row?.body ? `Your testimonial: ${STATUS[row.status] || 'Saved'}${row.status === 'approved' && !row.consent ? ' (not shown — no permission to publish)' : ''}` : '';
  };
  const choose = async (v, b) => {
    press(b); error.textContent = '';
    vote = v; showVote();
    const r = await service.vote(v); // saved as soon as a thumb is clicked
    if (!r.ok) error.textContent = r.error;
  };
  up.addEventListener('click', () => choose(1, up));
  down.addEventListener('click', () => choose(-1, down));
  text.addEventListener('input', () => { count.textContent = `${text.value.length} / ${MAX_BODY}`; error.textContent = ''; });
  send.addEventListener('click', async () => {
    press(send); error.textContent = '';
    if (vote === 1 && consent.checked && !text.value.trim()) { error.textContent = 'Write a sentence or two before sharing it publicly — or untick the box.'; text.focus(); return; }
    send.disabled = true;
    const r = await service.submit({ vote, body: text.value, displayName: nameIn.value, consent: consent.checked });
    send.disabled = false;
    if (!r.ok) { error.textContent = r.error; return; }
    thanksText.textContent = vote === 1 && consent.checked
      ? 'We review every testimonial before it appears on the website.'
      : 'Your feedback goes straight to the RouteZero team.';
    card.classList.add('done'); main.inert = true; thanks.removeAttribute('aria-hidden');
  });
  // the status line under the question is refreshed only when the form comes back, so the card keeps its height
  again.addEventListener('click', () => { setStatus({ body: text.value.trim(), status: 'pending', consent: consent.checked }); card.classList.remove('done'); main.inert = false; thanks.setAttribute('aria-hidden', 'true'); (vote === 1 ? up : down).focus(); });

  // what this learner already sent (prefill; nothing is shown to others from here)
  service.mine().then((row) => {
    if (!row || vote !== 0) return;
    vote = row.vote; text.value = row.body || ''; count.textContent = `${text.value.length} / ${MAX_BODY}`;
    if (row.display_name) nameIn.value = row.display_name;
    if (!consentTouched) consent.checked = !!row.consent;
    showVote(); setStatus(row);
  }).catch(() => {});
  showVote();
  return card;
}
